'use client';

import { Button } from '@ahafood/ui/components/button';
import { Card } from '@ahafood/ui/components/card';
import { X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigation } from './navigation/navigation-context';
import { useOrderListSheet } from '@/app/[locale]/order-list/order-list-sheet-context';
import { useBottomPosition } from '@/hooks/use-bottom-position';
import { DateFormat, formatUnixTime } from '@/lib/datetime';

interface OrderNotification {
  order_code: string;
  store_name: string;
  order_status: string;
  total_payment: number;
  time: number;
}

interface OrderNotificationToastProps {
  notification: OrderNotification | null;
  onClose: () => void;
}

export function OrderNotificationToast({
  notification,
  onClose,
}: OrderNotificationToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  const { openSheet } = useOrderListSheet();
  const { bottomPosition } = useBottomPosition();
  const { isNavigationVisible } = useNavigation();

  const formatTime = (timestamp: number) => {
    return formatUnixTime(timestamp, DateFormat.DATE_TIME_24);
  };

  useEffect(() => {
    if (notification) {
      setIsVisible(true);
    }
  }, [notification]);

  const getStatusInfo = (status: string) => {
    const statusMap = {
      CONFIRMING: { text: 'Đang chờ xác nhận' },
      CONFIRMED: { text: 'Đã xác nhận' },
      ASSIGNING: { text: 'Đang tìm tài xế' },
      ACCEPTED: { text: 'Tài xế đã nhận' },
      'IN PROCESS': { text: 'Đang giao đến bạn' },
      BOARDED: { text: 'Đang giao đến bạn' },
      COMPLETED: { text: 'Đã hoàn thành' },
      IN_RETURN: { text: 'Đang hoàn hàng' },
      RETURNED: { text: 'Đã hoàn hàng' },
      CANCELLED: { text: 'Đã hủy' },
      BOT_CANCELLED: { text: 'Hệ thống hủy' },
      FAILED: { text: 'Thất bại' },
      IDLE: { text: 'Đang tìm tài xế' },
    };
    return statusMap[status as keyof typeof statusMap] || { text: status };
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300); // Wait for animation to complete
  };

  if (!notification) return null;

  return (
    <Card
      className={`
        fixed inset-x-4 w-vw z-20 bg-white 
        rounded-lg shadow-lg transition-all
        duration-300 ease-in-out
        ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }
      `}
      onClick={(e) => {
        e.stopPropagation();
        openSheet(notification.order_code);
        setIsVisible(false);
      }}
      style={{ bottom: isNavigationVisible ? bottomPosition : 16 }}
    >
      <div className="p-4 w-full">
        {/* Header với close button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div>
              <p className="text-sm text-neutral-50">
                Đơn #{notification.order_code} {'  '}{' '}
                {formatTime(notification.time)}
              </p>
              <div className="flex items-center space-x-1 w-full">
                <p className="font-semibold text-neutral-90 line-clamp-1 w-auto">
                  {notification.store_name
                    ? notification.store_name
                    : 'Cửa hàng'}{' '}
                  —
                </p>
                <span className="text-primary-50 ml-1 font-semibold flex-none">
                  {getStatusInfo(notification.order_status).text}
                </span>
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            size="icon"
            className="rounded-full transition-colors [&_svg]:size-7 text-neutral-40 hover:bg-neutral-10 flex-none -mr-3"
          >
            <X />
          </Button>
        </div>

        {/* Status indicator */}
        {/* <div className="flex items-center space-x-2">
          <div className="flex space-x-1">
            <div className="size-2 bg-primary-50 rounded-full animate-pulse" />
            <div className="size-2 bg-primary-30 rounded-full" />
            <div className="size-2 bg-neutral-20 rounded-full" />
          </div>
          <span className="text-sm text-neutral-60">
            Trạng thái: {notification.order_status}
          </span>
        </div>

        <div className="mt-2">
          <p className="text-lg font-bold text-neutral-90">
            {formatCurrency(notification.total_payment)}
          </p>
        </div> */}
      </div>
    </Card>
  );
}
